import { useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Upload, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { UserAvatar } from "@/components/common/UserAvatar";

const MAX_BYTES = 2 * 1024 * 1024; // 2MB raw upload
const MAX_DIM = 512;
const ACCEPTED = ["image/png", "image/jpeg", "image/webp"];

async function resizeToWebp(file: File): Promise<Blob> {
  const dataUrl: string = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  const img: HTMLImageElement = await new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = reject;
    el.src = dataUrl;
  });
  const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported");
  ctx.drawImage(img, 0, 0, w, h);
  return await new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Encoding failed"))),
      "image/webp",
      0.9,
    );
  });
}

// Bucket is private. We store the storage PATH in profiles.avatar_url and
// mint a short-lived signed URL at render time via useAvatarUrl. No long-lived
// signed URLs are persisted anywhere.

export function ProfilePhotoCard() {
  const { user, profile, refreshProfile } = useAuth();
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const avatarPath = (profile as any)?.avatar_url as string | null | undefined;
  const displayName = [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") || null;

  const handlePick = () => inputRef.current?.click();

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (inputRef.current) inputRef.current.value = "";
    if (!file || !user) return;

    if (!ACCEPTED.includes(file.type)) {
      toast.error("Please upload a PNG, JPG, or WEBP image.");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("Image is larger than 2MB. Please choose a smaller file.");
      return;
    }

    setBusy(true);
    try {
      const blob = await resizeToWebp(file);
      const timestamp = Date.now();
      const path = `${user.id}/avatar-${timestamp}.webp`;

      // List existing files first so we can delete them AFTER a successful upload.
      const { data: existing } = await supabase.storage
        .from("avatars")
        .list(user.id, { limit: 100 });

      const { error: upErr } = await supabase.storage
        .from("avatars")
        .upload(path, blob, { contentType: "image/webp", upsert: false });
      if (upErr) throw upErr;

      // Store the storage PATH (not a signed URL). Signing happens at render.
      const { error: updErr } = await supabase
        .from("profiles")
        .update({ avatar_url: path })
        .eq("id", user.id);
      if (updErr) throw updErr;

      // Best-effort cleanup of previous avatar objects.
      if (existing && existing.length > 0) {
        const stale = existing
          .map((o) => `${user.id}/${o.name}`)
          .filter((p) => p !== path);
        if (stale.length > 0) {
          await supabase.storage.from("avatars").remove(stale);
        }
      }

      await refreshProfile();
      toast.success("Profile photo updated");
    } catch (err: any) {
      console.error("Avatar upload failed:", err);
      toast.error(err?.message || "Failed to upload photo");
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!user) return;
    setBusy(true);
    try {
      const { data: existing } = await supabase.storage
        .from("avatars")
        .list(user.id, { limit: 100 });
      if (existing && existing.length > 0) {
        await supabase.storage
          .from("avatars")
          .remove(existing.map((o) => `${user.id}/${o.name}`));
      }
      const { error } = await supabase
        .from("profiles")
        .update({ avatar_url: null })
        .eq("id", user.id);
      if (error) throw error;
      await refreshProfile();
      toast.success("Profile photo removed");
    } catch (err: any) {
      console.error("Avatar remove failed:", err);
      toast.error(err?.message || "Failed to remove photo");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-6 mb-6">
      <h2 className="text-xl font-semibold mb-4">Profile Photo</h2>
      <div className="flex items-center gap-6">
        <UserAvatar
          path={avatarPath}
          name={displayName}
          email={profile?.email}
          size="lg"
        />
        <div className="flex-1">
          <p className="text-sm text-muted-foreground mb-3">
            PNG, JPG, or WEBP. Max 2MB. Images are resized to 512×512.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button onClick={handlePick} disabled={busy} size="sm">
              {busy ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              {avatarPath ? "Replace Photo" : "Upload Photo"}
            </Button>
            {avatarPath && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRemove}
                disabled={busy}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Remove
              </Button>
            )}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={handleFile}
            className="hidden"
          />
        </div>
      </div>
    </Card>
  );
}

export default ProfilePhotoCard;
